import React, {
  useState,
  useEffect,
  useCallback
} from 'react';
import type { FC, ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import { useDispatch } from 'src/store';
import useAuth from 'src/hooks/useAuth';
import { fetchPackageTypesDict } from 'src/slices/app';
import type { Theme } from 'src/theme';
import NavBar from './NavBar';
import TopBar from './TopBar';

interface DashboardLayoutProps {
  children?: ReactNode;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    display: 'flex',
    height: '100%',
    overflow: 'hidden',
    width: '100%',
  },
  wrapper: {
    display: 'flex',
    flex: '1 1 auto',
    overflow: 'hidden',
    paddingTop: 64,
    [theme.breakpoints.up('lg')]: {
      paddingLeft: 256,
    },
  },
  contentContainer: {
    display: 'flex',
    flex: '1 1 auto',
    overflow: 'hidden',
  },
  content: {
    flex: '1 1 auto',
    height: '100%',
    overflow: 'auto',
  },
}));

const DashboardLayout: FC<DashboardLayoutProps> = ({ children }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [isMobileNavOpen, setMobileNavOpen] = useState<boolean>(false);

  const handleMobileOpen = useCallback(
    () => setMobileNavOpen(true),
    [setMobileNavOpen],
  );

  const handleMobileClose = useCallback(
    () => setMobileNavOpen(false),
    [setMobileNavOpen],
  );

  const handleLogout = useCallback(
    () => {
      try {
        logout();
        history.push('/');
      } catch (err) {
        console.error(err);
        enqueueSnackbar('Возникла ошибка при выходе из системы', {
          variant: 'error',
        });
      }
    }, [logout, history, enqueueSnackbar],
  );

  useEffect(
    () => {
      dispatch(fetchPackageTypesDict());
    },
    [dispatch]
  );

  return (
    <div className={classes.root}>
      <TopBar
        onMobileNavOpen={handleMobileOpen}
        onLogoutClick={handleLogout}
      />
      <NavBar
        onMobileClose={handleMobileClose}
        openMobile={isMobileNavOpen}
      />
      <div className={classes.wrapper}>
        <div className={classes.contentContainer}>
          <div className={classes.content}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

DashboardLayout.propTypes = {
  children: PropTypes.node,
};

export default DashboardLayout;
